import {
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  Tooltip,
  ModalContent,
  ModalOverlay,
  Button,
  Text,
  VStack,
  Box,
  HStack,
  IconButton,
  FormControl,
  FormLabel,
  Checkbox,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  useToast,
  Icon,
  ModalFooter,
  Stack,
  Textarea,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { useEffect, useState, useCallback } from 'react';
import { HelivanSelector } from '../helivan/HelivanSelector';
import {
  DtType,
  ProjectsDocument,
  ProjectsQuery,
  UpdateFrequency,
  NotificationChannel,
  NotificationFrequency,
  useConfirmSubtopicsMutation,
  useCreateNewProjectModalQuery,
  useCreateProjectMutation,
  useGenerateProjectMutation,
  useGetMyAgentCollectionsQuery,
  useGetCustomAgentQuery,
  useGetMonitoringTopicsQuery,
  useUpdateProjectSettingsMutation,
  useGeneratePlanningTasksMutation,
  useSuggestWatchTitleMutation,
  useCreatePlanningProbeMutation,
  useGetMyCustomAgentsQuery,
  useListConnectorSessionsLazyQuery,
} from '../../generated/graphql';
import { FaPlus, FaTrash, FaUpload } from 'react-icons/fa';
import { RiWifiOffLine, RiInformationLine, RiRobot2Line, RiTerminalBoxLine, RiSparkling2Line } from 'react-icons/ri';
import { CreateCollectionModal } from '../MyAgents/CreateCollectionModal';
import { HelivanText } from '../helivan/HelivanText';
import { getDtTypeString } from '../../utils/getDtTypeString';
import { estimateRunTokens, runsPerMonth, humanizeTokens } from '../../utils/tokenEstimate';
import { formatVersionLabel } from '../../utils/formatDate';
import { HelivanColor } from '../helivan/HelivanColor';
import CreateChatLoading from './CreateChatLoading';
import { useDropzone } from 'react-dropzone';
import config from '../../config';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { StyledSelect } from '../helivan/StyledSelect';

// Small inline info tooltip used to explain settings + give a recommendation.
const SettingHint = ({ label }: { label: string }) => (
  <Tooltip label={label} hasArrow placement="top" maxW="260px" fontSize="12px">
    <Box as="span" display="inline-flex" alignItems="center" ml={1} color="#94a3b8" cursor="help">
      <RiInformationLine size={13} />
    </Box>
  </Tooltip>
);

interface CreateNewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasTokens: boolean;
  // Optional props for pre-selection (from card actions)
  preSelectedDigitalTwinId?: string;
  preSelectedDigitalTwinName?: string;
  preSelectedDigitalTwinType?: DtType;
  preSelectedCustomAgentId?: string;
  preSelectedCustomAgentName?: string;
  preSelectedCollectionId?: string;
  preSelectedCollectionName?: string;
}

export const CreateNewProjectModal = ({
  isOpen,
  onClose,
  hasTokens,
  preSelectedDigitalTwinId,
  preSelectedDigitalTwinName,
  preSelectedDigitalTwinType,
  preSelectedCustomAgentId,
  preSelectedCustomAgentName,
  preSelectedCollectionId,
  preSelectedCollectionName,
}: CreateNewProjectModalProps) => {
  const { data } = useCreateNewProjectModalQuery();
  // Top-level fork on the first step: pick what kind of agent to watch. null = show
  // the fork; 'personal' reveals the existing collection flow; 'coding' opens the
  // inline planning-probe flow (described below).
  const [watchKind, setWatchKind] = useState<'personal' | 'coding' | 'persona' | null>(null);
  const [viewType, setViewType] = useState<'form' | 'form-part2' | 'settings' | 'confirm'>(
    'form'
  );

  // Personal-agent path: a custom agent monitored directly (no collection needed).
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  // Personal-agent wizard sub-step: pick the agent/collection, then the topics.
  const [personalStep, setPersonalStep] = useState<'select' | 'topics'>('select');

  // --- Planning-probe (coding agent) wizard: pick agent → describe → review ---
  const [planningStep, setPlanningStep] = useState<'agent' | 'session' | 'describe' | 'review'>('agent');
  const [planningDescription, setPlanningDescription] = useState('');
  // Coding watch is pinned to one Claude Code session, chosen after the agent.
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  // AI-suggested, user-editable watch name (shown on the review screen).
  const [planningTitle, setPlanningTitle] = useState('');
  // Optional user title for personal/persona watches (display name).
  const [watchTitle, setWatchTitle] = useState('');
  // Tasks the user has unchecked on the review screen (excluded from the watch).
  const [excludedTaskIdx, setExcludedTaskIdx] = useState<Set<number>>(new Set());
  const [planningTasks, setPlanningTasks] = useState<{ name: string; task: string; prompt: string }[]>([]);
  const [planningAgentIds, setPlanningAgentIds] = useState<Set<string>>(new Set());
  const [generatePlanningTasks, { loading: generatingTasks }] = useGeneratePlanningTasksMutation();
  const [suggestWatchTitle] = useSuggestWatchTitleMutation();
  const [createPlanningProbe, { loading: creatingProbe }] = useCreatePlanningProbeMutation();
  const [loadSessions, { data: sessionsData, loading: sessionsLoading, error: sessionsError }] =
    useListConnectorSessionsLazyQuery({ fetchPolicy: 'network-only' });
  const { data: myAgentsData } = useGetMyCustomAgentsQuery({
    variables: { ownedOnly: false },
  });
  const planningAgents = myAgentsData?.getMyCustomAgents ?? [];
  const [generateProject] = useGenerateProjectMutation();
  const [createProject] = useCreateProjectMutation();
  const [doConfirmSubtopics, { loading: doConfirmSubtopicsLoading }] =
    useConfirmSubtopicsMutation();
  // State for form fields
  const [topic, setTopic] = useState('');
  const [subtopics, setSubtopics] = useState<string[]>(['']);
  // Add state for confirmation view
  const [confirmTopic, setConfirmTopic] = useState('');
  const [confirmSubtopics, setConfirmSubtopics] = useState<string[]>([]);
  const [selectedDtType, setSelectedDtType] = useState<DtType | null>(null);
  const [selectedDemographics, setSelectedDemographics] = useState<
    Set<{ key: string; value: string }>
  >(new Set());
  const [selectedNiches, setSelectedNiches] = useState<Set<string>>(new Set());
  const [selectedFollowerRanges, setSelectedFollowerRanges] = useState<
    Set<string>
  >(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [dtTypeOptions, setDtTypeOptions] = useState<
    { key: DtType; value: string }[]
  >([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [createProjectLoading, setCreateProjectLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [initialTopic, setInitialTopic] = useState('');
  const [initialSubtopics, setInitialSubtopics] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [createCollectionOpen, setCreateCollectionOpen] = useState(false);

  // Monitoring topics state - multiple subtopic selection (up to 5)
  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(new Set());
  const [selectedSubtopicIds, setSelectedSubtopicIds] = useState<Set<string>>(new Set());

  // Project settings state
  const [updateFrequency, setUpdateFrequency] = useState<UpdateFrequency>(UpdateFrequency.Manual);
  const [customUpdateIntervalHours, setCustomUpdateIntervalHours] = useState<number>(24);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(false);
  const [changeDetectionEnabled, setChangeDetectionEnabled] = useState(true);
  const [sensitivityStdDevs, setSensitivityStdDevs] = useState(2.0);
  const [burnInPeriod, setBurnInPeriod] = useState(3);
  // Monitoring emails — mirrors the watch Settings modal: a single notificationFrequency
  // choice (+ digest interval) drives the derived per-event flags.
  const [notificationFrequency, setNotificationFrequency] = useState<NotificationFrequency>(NotificationFrequency.EveryUpdate);
  const [summaryIntervalHours, setSummaryIntervalHours] = useState<number>(24);
  const [notificationEmail, setNotificationEmail] = useState('');

  // The per-event flags the API still expects, derived from the chosen frequency
  // (kept identical to the Settings modal's save).
  const notificationsEnabled = notificationFrequency !== NotificationFrequency.Never;
  const notificationSettingsPayload = {
    notificationFrequency,
    summaryIntervalHours: notificationFrequency === NotificationFrequency.Interval ? summaryIntervalHours : undefined,
    notificationsEnabled,
    notifyOnCompletion: notificationFrequency === NotificationFrequency.EveryUpdate,
    notifyOnErrors: false,
    notifyOnNewInsights: notificationFrequency === NotificationFrequency.OnChange,
    notificationChannels: [NotificationChannel.Email],
    notificationEmail: notificationEmail || undefined,
  };

  // Update settings mutation
  const [updateProjectSettings] = useUpdateProjectSettingsMutation();

  // Collection query
  const { data: collectionsData, refetch: refetchCollections } = useGetMyAgentCollectionsQuery();

  // Fetch pre-selected agent status (for single agent project creation)
  const { data: preSelectedAgentData } = useGetCustomAgentQuery({
    variables: { id: preSelectedCustomAgentId! },
    skip: !preSelectedCustomAgentId,
  });

  // Monitoring topics query - always fetch (topics are universal)
  const { data: monitoringTopicsData, loading: topicsLoading } = useGetMonitoringTopicsQuery({
    variables: {},
  });
  const monitoringTopics = monitoringTopicsData?.getMonitoringTopics ?? [];

  const collections = collectionsData?.getMyAgentCollections ?? [];
  const collectionOptions = [
    { value: '', label: 'None' },
    ...collections.map((c) => ({ value: c.id, label: c.name })),
  ];

  // Check if any agents are offline (for collection or pre-selected agent)
  const getOfflineAgents = (): { name: string }[] => {
    // Check pre-selected custom agent
    if (preSelectedCustomAgentId && preSelectedAgentData?.getCustomAgent) {
      const agent = preSelectedAgentData.getCustomAgent;
      const isOnline = agent.connectionType === 'CONNECTOR'
        ? agent.connectorStatus === 'ONLINE' && agent.gatewayHealth !== 'UNREACHABLE'
        : agent.status === 'ACTIVE';
      if (!isOnline) return [{ name: agent.name }];
    }
    // Check a directly-selected personal agent
    if (selectedAgentId) {
      const agent = (myAgentsData?.getMyCustomAgents ?? []).find((a) => a.id === selectedAgentId);
      if (agent) {
        const isOnline = agent.connectionType === 'CONNECTOR'
          ? agent.connectorStatus === 'ONLINE' && agent.gatewayHealth !== 'UNREACHABLE'
          : agent.status === 'ACTIVE';
        if (!isOnline) return [{ name: agent.name }];
      }
    }
    // Check agents in selected collection
    if (selectedCollectionId) {
      const collection = collections.find(c => c.id === selectedCollectionId);
      if (collection?.agents) {
        return collection.agents.filter((agent) => {
          const isOnline = agent.connectionType === 'CONNECTOR'
            ? agent.connectorStatus === 'ONLINE' && agent.gatewayHealth !== 'UNREACHABLE'
            : agent.status === 'ACTIVE';
          return !isOnline;
        }).map(a => ({ name: a.name }));
      }
    }
    return [];
  };
  const offlineAgents = getOfflineAgents();
  const hasOfflineAgents = offlineAgents.length > 0;

  useEffect(() => {
    if (data?.user?.currentOrganization?.personaAccessTypes) {
      setDtTypeOptions(
        data.user.currentOrganization.personaAccessTypes.map(
          (personaAccessType) => ({
            key: personaAccessType,
            value: getDtTypeString(personaAccessType),
          })
        )
      );
    }
  }, [data]);

  // Initialize state based on pre-selected props
  useEffect(() => {
    if (isOpen && preSelectedCollectionId) {
      setSelectedCollectionId(preSelectedCollectionId);
    }
  }, [isOpen, preSelectedCollectionId]);

  // Determine if we have a pre-selected agent/persona/collection
  const hasPreSelection = !!(preSelectedDigitalTwinId || preSelectedCustomAgentId || preSelectedCollectionId);
  const preSelectionName = preSelectedDigitalTwinName || preSelectedCustomAgentName || preSelectedCollectionName;
  // A custom agent was pre-selected (from an agent card): it could be a coding agent,
  // so we still offer the coding-vs-personal fork (DT/collection pre-selections don't).
  const agentPreSelection = !!preSelectedCustomAgentId;
  // One-thing-per-screen wizard. Exactly one of these is the current screen on the
  // 'form' viewType (settings/confirm are separate viewTypes downstream).
  // A DT/collection pre-selection skips the fork + select and lands on topics.
  const dtCollectionPreselect = hasPreSelection && !agentPreSelection;
  const screenFork = !watchKind && (!hasPreSelection || agentPreSelection);
  const screenPersonalSelect =
    watchKind === 'personal' && personalStep === 'select' && !dtCollectionPreselect && !agentPreSelection;
  // Persona path: pick a curated, platform-built persona, then the shared topics step.
  const screenPersonaSelect = watchKind === 'persona' && personalStep === 'select';
  const screenPersonalTopics =
    dtCollectionPreselect ||
    ((watchKind === 'personal' || watchKind === 'persona') && personalStep === 'topics');
  const screenCodingAgent = watchKind === 'coding' && planningStep === 'agent' && !agentPreSelection;
  const screenCodingSession = watchKind === 'coding' && planningStep === 'session';
  const screenCodingDescribe = watchKind === 'coding' && planningStep === 'describe';
  const screenCodingReview = watchKind === 'coding' && planningStep === 'review';

  // Handle adding a new subtopic
  const addSubtopic = () => {
    if (subtopics.length < 5) {
      setSubtopics([...subtopics, '']);
    }
  };

  // Handle removing a subtopic
  const removeSubtopic = (index: number) => {
    const newSubtopics = [...subtopics];
    newSubtopics.splice(index, 1);
    setSubtopics(newSubtopics);
  };

  // Handle updating a subtopic
  const updateSubtopic = (index: number, value: string) => {
    const newSubtopics = [...subtopics];
    newSubtopics[index] = value;
    setSubtopics(newSubtopics);
  };

  // Helper function to get demographics data based on DtType
  const getDemographicsForType = (
    dtType: DtType
  ): Record<string, Array<{ display: string; value: string }>> => {
    if (dtType === DtType.Congressperson) {
      return {
        party: ['Democrat', 'Republican', 'Independent'].map((value) => ({
          display: value,
          value,
        })),
        chamber: [
          { display: 'House', value: 'house' },
          { display: 'Senate', value: 'senate' },
        ],
        state: [
          { display: 'Alabama', value: 'al' },
          { display: 'Alaska', value: 'ak' },
          { display: 'Arizona', value: 'az' },
          { display: 'Arkansas', value: 'ar' },
          { display: 'California', value: 'ca' },
          { display: 'Colorado', value: 'co' },
          { display: 'Connecticut', value: 'ct' },
          { display: 'Delaware', value: 'de' },
          { display: 'Florida', value: 'fl' },
          { display: 'Georgia', value: 'ga' },
          { display: 'Hawaii', value: 'hi' },
          { display: 'Idaho', value: 'id' },
          { display: 'Illinois', value: 'il' },
          { display: 'Indiana', value: 'in' },
          { display: 'Iowa', value: 'ia' },
          { display: 'Kansas', value: 'ks' },
          { display: 'Kentucky', value: 'ky' },
          { display: 'Louisiana', value: 'la' },
          { display: 'Maine', value: 'me' },
          { display: 'Maryland', value: 'md' },
          { display: 'Massachusetts', value: 'ma' },
          { display: 'Michigan', value: 'mi' },
          { display: 'Minnesota', value: 'mn' },
          { display: 'Mississippi', value: 'ms' },
          { display: 'Missouri', value: 'mo' },
          { display: 'Montana', value: 'mt' },
          { display: 'Nebraska', value: 'ne' },
          { display: 'Nevada', value: 'nv' },
          { display: 'New Hampshire', value: 'nh' },
          { display: 'New Jersey', value: 'nj' },
          { display: 'New Mexico', value: 'nm' },
          { display: 'New York', value: 'ny' },
          { display: 'North Carolina', value: 'nc' },
          { display: 'North Dakota', value: 'nd' },
          { display: 'Ohio', value: 'oh' },
          { display: 'Oklahoma', value: 'ok' },
          { display: 'Oregon', value: 'or' },
          { display: 'Pennsylvania', value: 'pa' },
          { display: 'Rhode Island', value: 'ri' },
          { display: 'South Carolina', value: 'sc' },
          { display: 'South Dakota', value: 'sd' },
          { display: 'Tennessee', value: 'tn' },
          { display: 'Texas', value: 'tx' },
          { display: 'Utah', value: 'ut' },
          { display: 'Vermont', value: 'vt' },
          { display: 'Virginia', value: 'va' },
          { display: 'Washington', value: 'wa' },
          { display: 'West Virginia', value: 'wv' },
          { display: 'Wisconsin', value: 'wi' },
          { display: 'Wyoming', value: 'wy' },
        ],
      };
    } else if (dtType === DtType.MoviesSimple) {
      return {
        gender: ['Female', 'Male'].map((value) => ({ display: value, value })),
      };
    } else if (dtType === DtType.MoviesFull || dtType === DtType.MoviesMedium) {
      return {
        gender: ['Female', 'Male'].map((value) => ({ display: value, value })),
        ethnicity: [
          'American Indian or Alaska Native',
          'Asian',
          'Black or African American',
          'Hispanic',
          'Pacific Islander',
          'White',
        ].map((value) => ({ display: value, value })),
        region: ['Midwest', 'Northeast', 'South', 'West'].map((value) => ({
          display: value,
          value,
        })),
      };
    } else if (dtType === DtType.EarlyStageVcs) {
      return {
        location: [
          'Atlanta',
          'Austin',
          'Europe',
          'Boston',
          'Chicago',
          'Israel',
          'Los Angeles',
          'Miami',
          'New York',
          'Philadelphia',
          'San Francisco Bay Area',
          'Seattle',
          'London',
          'Washington, DC',
        ].map((value) => ({ display: value, value })),
      };
    }

    return {};
  };

  // Get demographics intersection for multiple DT types (for mixed collections)
  const getDemographicsIntersection = (
    dtTypes: DtType[]
  ): Record<string, Array<{ display: string; value: string }>> => {
    if (dtTypes.length === 0) return {};
    if (dtTypes.length === 1) return getDemographicsForType(dtTypes[0]);

    // Get demographics for each type
    const allDemographics = dtTypes.map(getDemographicsForType);

    // Find common demographic keys across all types
    const commonKeys = Object.keys(allDemographics[0]).filter(key =>
      allDemographics.every(demo => key in demo)
    );

    // For each common key, find the intersection of values
    const result: Record<string, Array<{ display: string; value: string }>> = {};
    for (const key of commonKeys) {
      const firstValues = allDemographics[0][key];
      const commonValues = firstValues.filter(item =>
        allDemographics.every(demo =>
          demo[key].some(v => v.value === item.value)
        )
      );
      if (commonValues.length > 0) {
        result[key] = commonValues;
      }
    }

    return result;
  };

  // Get the DT types from the selected collection
  const getCollectionDtTypes = (): DtType[] => {
    if (!selectedCollectionId) return [];
    const collection = collections.find(c => c.id === selectedCollectionId);
    if (!collection?.digitalTwins) return [];

    const types = new Set<DtType>();
    for (const dt of collection.digitalTwins) {
      if (dt.type) {
        types.add(dt.type as DtType);
      }
    }
    return Array.from(types);
  };

  // Get active demographics based on selectedDtType or collection
  const getDemographics = (
    dtType: DtType | null
  ): Record<string, Array<{ display: string; value: string }>> => {
    if (dtType) {
      return getDemographicsForType(dtType);
    }

    // For collection-based projects, compute intersection
    const collectionTypes = getCollectionDtTypes();
    return getDemographicsIntersection(collectionTypes);
  };

  // Check if demographics are available (for selected DtType or collection)
  const hasDemographicsAvailable = (): boolean => {
    const demographics = getDemographics(selectedDtType);
    return Object.keys(demographics).length > 0;
  };

  // Function to get display text for a demographic value
  const getDemographicDisplay = (key: string, value: string): string => {
    const demographics = getDemographics(selectedDtType);
    const category = demographics[key];
    if (!category) return value;
    const item = category.find((item) => item.value === value);
    return item ? item.display : value;
  };

  // Helper function to check if a specific demographic is selected
  const isDemographicSelected = (key: string, value: string): boolean => {
    return Array.from(selectedDemographics).some(
      (item) => item.key === key && item.value === value
    );
  };

  // Helper function to toggle a single demographic
  const toggleDemographic = (key: string, value: string) => {
    const newSelectedDemographics = new Set(selectedDemographics);
    const existingItem = Array.from(newSelectedDemographics).find(
      (item) => item.key === key && item.value === value
    );

    if (existingItem) {
      // If item exists, remove it
      newSelectedDemographics.delete(existingItem);
    } else {
      // If item doesn't exist, add it
      newSelectedDemographics.add({ key, value });
    }

    setSelectedDemographics(newSelectedDemographics);
  };

  // Function to toggle all demographics in a category
  const toggleCategoryDemographics = (key: string, selected: boolean) => {
    if (!hasDemographicsAvailable()) return;

    const newSelectedDemographics = new Set(selectedDemographics);
    const demographics = getDemographics(selectedDtType);

    // Remove existing items in this category
    Array.from(newSelectedDemographics)
      .filter((item) => item.key === key)
      .forEach((item) => newSelectedDemographics.delete(item));

    // If selected is true, add all items from this category
    if (selected && demographics[key] && demographics[key].length > 0) {
      demographics[key].forEach((item) => {
        newSelectedDemographics.add({ key, value: item.value });
      });
    }

    setSelectedDemographics(newSelectedDemographics);
  };

  // Function to check if all demographics in a category are selected
  const isCategoryFullySelected = (key: string): boolean => {
    if (!hasDemographicsAvailable()) return false;

    const demographics = getDemographics(selectedDtType);
    const categoryValues = demographics[key] || [];
    const selectedInCategory = Array.from(selectedDemographics).filter(
      (item) => item.key === key
    );

    return (
      selectedInCategory.length === categoryValues.length &&
      categoryValues.length > 0
    );
  };

  // Function to check if some (but not all) demographics in a category are selected
  const isCategoryPartiallySelected = (key: string): boolean => {
    if (!hasDemographicsAvailable()) return false;

    const demographics = getDemographics(selectedDtType);
    const categoryValues = demographics[key] || [];
    const selectedInCategory = Array.from(selectedDemographics).filter(
      (item) => item.key === key
    );

    return (
      selectedInCategory.length > 0 &&
      selectedInCategory.length < categoryValues.length
    );
  };

  // Function to select all demographics
  const selectAllDemographics = () => {
    if (!hasDemographicsAvailable()) return;

    const demographics = getDemographics(selectedDtType);
    const newSelectedDemographics = new Set<{ key: string; value: string }>();

    // For each category, select all values
    Object.entries(demographics).forEach(([key, values]) => {
      values.forEach((item) => {
        newSelectedDemographics.add({ key, value: item.value });
      });
    });

    setSelectedDemographics(newSelectedDemographics);
  };

  // Function to get display text for the demographics button
  const getDemographicsButtonText = (): string => {
    if (selectAll) {
      return 'All';
    }

    if (!selectedDemographics || selectedDemographics.size === 0) {
      return 'Select Demographics';
    }

    if (selectedDemographics.size === 1) {
      const item = Array.from(selectedDemographics)[0];
      return getDemographicDisplay(item.key, item.value);
    }

    // Group selected demographics by key
    const selectedByCategory: Record<string, string[]> = {};
    Array.from(selectedDemographics).forEach(({ key, value }) => {
      if (!selectedByCategory[key]) {
        selectedByCategory[key] = [];
      }
      selectedByCategory[key].push(getDemographicDisplay(key, value));
    });

    return Object.entries(selectedByCategory)
      .map(([key, values]) => `${values.join(', ')}`)
      .join(', ');
  };

  // Handle DtType change - reset all selections
  const handleDtTypeSelect = (dtType: DtType) => {
    setSelectedDtType(dtType);
    setSelectedAgentId(null);
    setSelectedDemographics(new Set());
    setSelectedNiches(new Set());
    setSelectedFollowerRanges(new Set());
    setSelectAll(false);
  };

  // Helper functions for TikToker niches
  const toggleNiche = (niche: string) => {
    const newSelectedNiches = new Set(selectedNiches);
    if (newSelectedNiches.has(niche)) {
      newSelectedNiches.delete(niche);
    } else {
      newSelectedNiches.add(niche);
    }
    setSelectedNiches(newSelectedNiches);
  };

  // Helper functions for TikToker follower ranges
  const toggleFollowerRange = (range: string) => {
    const newSelectedFollowerRanges = new Set(selectedFollowerRanges);
    if (newSelectedFollowerRanges.has(range)) {
      newSelectedFollowerRanges.delete(range);
    } else {
      newSelectedFollowerRanges.add(range);
    }
    setSelectedFollowerRanges(newSelectedFollowerRanges);
  };

  // Helper functions for monitoring topics - multi subtopic selection (up to 5)
  const toggleTopic = (topicId: string) => {
    const newSelectedTopicIds = new Set(selectedTopicIds);
    if (newSelectedTopicIds.has(topicId)) {
      newSelectedTopicIds.delete(topicId);
    } else {
      newSelectedTopicIds.add(topicId);
    }
    setSelectedTopicIds(newSelectedTopicIds);
  };

  const toggleSubtopic = (subtopicId: string, topicId: string) => {
    const newSelectedSubtopicIds = new Set(selectedSubtopicIds);

    if (newSelectedSubtopicIds.has(subtopicId)) {
      // Deselect subtopic
      newSelectedSubtopicIds.delete(subtopicId);
    } else {
      // Check if we've reached the limit
      if (newSelectedSubtopicIds.size >= 5) {
        return; // Don't add more than 5
      }
      // Select subtopic
      newSelectedSubtopicIds.add(subtopicId);
    }

    setSelectedSubtopicIds(newSelectedSubtopicIds);

    // Ensure topic is expanded
    if (!selectedTopicIds.has(topicId)) {
      const newSelectedTopicIds = new Set(selectedTopicIds);
      newSelectedTopicIds.add(topicId);
      setSelectedTopicIds(newSelectedTopicIds);
    }
  };

  // Get all selected subtopics with their data
  const getSelectedSubtopics = () => {
    const subtopics: Array<{ id: string; name: string; spectrumLeftTitle: string; spectrumRightTitle: string; questions: string[] }> = [];
    for (const topic of monitoringTopics) {
      for (const st of topic.subtopics) {
        if (selectedSubtopicIds.has(st.id)) {
          subtopics.push(st);
        }
      }
    }
    return subtopics;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
    },
  });

  useAuth();

  // Handle form submission
  const handleCreateProject = async () => {
    // Block project creation if any agents are offline
    if (hasOfflineAgents) {
      toast({
        title: 'Agents unavailable',
        description: 'All agents must be online with a reachable gateway to create a watch.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check if using collection-based or dtType-based project
    const isCollectionBased = !!selectedCollectionId && !selectedDtType;
    const isDtTypeBased = !!selectedDtType;
    const isPreSelectedAgent = !!(preSelectedDigitalTwinId || preSelectedCustomAgentId);
    const isDirectAgent = !!selectedAgentId;

    // Validate required fields - need an agent, collection, dtType, or pre-selection
    if (!isDtTypeBased && !isCollectionBased && !isPreSelectedAgent && !isDirectAgent) {
      toast({
        title: 'Error',
        description: 'Please select an agent or collection.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (selectedSubtopicIds.size === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one topic.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Build project data before closing modal
    let projectData: any = {};

    // Add dtType-based data if using dtType
    if (isDtTypeBased) {
      // For Congressperson, default to US Senators only (smaller, faster collection)
      const isCongressperson = selectedDtType === DtType.Congressperson;
      projectData = {
        dtType: selectedDtType,
        selectedDemographics: isCongressperson
          ? [{ key: 'chamber', value: 'senate' }]
          : [],
        selectedAll: !isCongressperson,
      };
    }

    // Add collection-based data
    if (selectedCollectionId) {
      projectData.collectionId = selectedCollectionId;
    }

    // Add pre-selected agent IDs
    if (preSelectedDigitalTwinId) {
      projectData.digitalTwinId = preSelectedDigitalTwinId;
      // Include the digital twin's type if available
      if (preSelectedDigitalTwinType) {
        projectData.dtType = preSelectedDigitalTwinType;
      }
    }
    if (preSelectedCustomAgentId) {
      projectData.customAgentId = preSelectedCustomAgentId;
    }
    // Directly-selected personal agent (no collection)
    if (selectedAgentId && !preSelectedCustomAgentId) {
      projectData.customAgentId = selectedAgentId;
    }

    // Add subtopicIds for multi-selection
    const selectedSubtopics = getSelectedSubtopics();
    if (selectedSubtopics.length > 0) {
      projectData = {
        ...projectData,
        subtopicIds: Array.from(selectedSubtopicIds),
        topic: selectedSubtopics.map(st => st.name).join(', '),
        subtopics: selectedSubtopics.map(st => st.name),
      };
    } else if (topic) {
      // Fallback to manual topic entry
      projectData = {
        ...projectData,
        topic,
        subtopics: subtopics.filter((subtopic) => subtopic.trim() !== ''),
      };
    }

    // Optional user-chosen display title (overrides the topic-derived name).
    if (watchTitle.trim()) {
      projectData = { ...projectData, title: watchTitle.trim() };
    }

    // Capture settings values before closing modal
    const settingsData = {
      updateFrequency,
      customUpdateIntervalHours: updateFrequency === UpdateFrequency.Custom ? customUpdateIntervalHours : undefined,
      autoUpdateEnabled,
      changeDetectionEnabled,
      sensitivityStdDevs,
      burnInPeriod,
      ...notificationSettingsPayload,
    };
    const capturedSelectedSubtopics = selectedSubtopics;
    const capturedSelectedFile = selectedFile;

    // Close modal IMMEDIATELY and show toast
    toast({
      title: 'Creating watch',
      description: 'Your watch is being created...',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    onClose();

    // Run everything else in background (fire-and-forget)
    (async () => {
      try {
        const result = await createProject({
          variables: {
            input: projectData,
          },
          // The Watches list reads the ProjectTemplates query; refetch it so the new
          // watch card appears without a manual refresh.
          refetchQueries: ['ProjectTemplates'],
          update: (cache, { data }) => {
            if (!data?.createProject) {
              return;
            }

            try {
              // Read the existing query
              const existingData = cache.readQuery({
                query: ProjectsDocument,
              }) as ProjectsQuery;

              if (existingData?.user?.currentOrganization?.initialProjects) {
                const newData = {
                  ...existingData,
                  user: {
                    ...existingData.user,
                    currentOrganization: {
                      ...existingData.user.currentOrganization,
                      initialProjects: [
                        data.createProject,
                        ...existingData.user.currentOrganization.initialProjects,
                      ],
                    },
                  },
                };

                // Write back the query with the new project added
                cache.writeQuery({
                  query: ProjectsDocument,
                  data: newData,
                });
              }
            } catch (error) {
              console.error('💥 Cache update error:', error);
            }
          },
        });

        if (!result.data?.createProject?.id) {
          throw new Error('Failed to create project - no ID returned');
        }

        const createdProjectId = result.data.createProject.id;
        console.log('✅ Project created with ID:', createdProjectId);

        // Show success toast
        toast({
          title: 'Watch created',
          description: 'Your watch is now being processed.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Apply project settings (settings live on the template)
        const createdTemplateId = result.data.createProject.projectTemplateId;
        if (createdTemplateId) {
          updateProjectSettings({
            variables: {
              input: {
                projectTemplateId: createdTemplateId,
                ...settingsData,
              },
            },
          }).catch((settingsError) => {
            console.error('Failed to apply project settings:', settingsError);
          });
        }

        // Handle file upload in background if needed
        if (capturedSelectedFile) {
          try {
            const formData = new FormData();
            formData.append('file', capturedSelectedFile);

            const auth = getAuth();
            const user = auth.currentUser;
            let token = '';

            if (user) {
              try {
                token = await user.getIdToken();
              } catch (error) {
                console.error('Error getting auth token:', error);
              }
            }

            const baseUrl = config.reactApp.apiUrl || 'http://localhost:4000';
            const apiBaseUrl = baseUrl.replace(/\/graphql$/, '');

            await fetch(
              `${apiBaseUrl}/api/project-files/upload/${createdProjectId}`,
              {
                method: 'POST',
                headers: {
                  Authorization: token ? `Bearer ${token}` : '',
                },
                body: formData,
              }
            );
          } catch (uploadError) {
            console.error('File upload failed:', uploadError);
          }
        }

        // Trigger generation in background if using predefined subtopics
        if (capturedSelectedSubtopics.length > 0) {
          doConfirmSubtopics({
            variables: {
              input: {
                projectId: createdProjectId,
                topic: capturedSelectedSubtopics.map(st => st.name).join(', '),
                subtopics: capturedSelectedSubtopics.map(st => st.name),
                subtopicsChanged: false,
              },
            },
          }).catch((confirmError) => {
            console.error('Failed to trigger generation:', confirmError);
          });
        }
      } catch (error: any) {
        console.error('Background project creation failed:', error);
        // Extract user-friendly error message from GraphQL error
        const graphQLError = error?.graphQLErrors?.[0];
        const errorMessage = graphQLError?.extensions?.userMessage
          || graphQLError?.message
          || error?.message
          || 'Failed to create project. Please try again.';

        // Check if it's a quota exceeded error
        const isQuotaError = graphQLError?.extensions?.code === 'QUOTA_EXCEEDED';

        toast({
          title: isQuotaError ? 'Usage Limit Reached' : 'Error',
          description: errorMessage,
          status: 'error',
          duration: isQuotaError ? 8000 : 5000,
          isClosable: true,
        });
      }
    })();
  };

  const handleConfirm = async () => {
    if (!projectId) {
      toast({
        title: 'Error',
        description: 'Project ID not found. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Compare initial and edited values to determine if changes were made
    const subtopicsChanged =
      confirmTopic !== initialTopic ||
      confirmSubtopics.length !== initialSubtopics.length ||
      confirmSubtopics.some(
        (subtopic, index) => subtopic !== initialSubtopics[index]
      );

    await doConfirmSubtopics({
      variables: {
        input: {
          projectId,
          topic: confirmTopic,
          subtopics: confirmSubtopics,
          subtopicsChanged,
        },
      },
      onCompleted: () => {
        toast({
          title: 'Success',
          description:
            "Watch creation has started. Watches can take up to 8 hours depending on demand. You will receive an email when it's ready.",
          status: 'success',
          duration: 8000,
          isClosable: true,
        });
        customOnClose();
      },
    });
  };

  const toast = useToast();

  const planningAgentOnline = (agent: {
    connectionType?: string | null;
    connectorStatus?: string | null;
    gatewayHealth?: string | null;
    status?: string | null;
  }) =>
    agent.connectionType === 'CONNECTOR'
      ? agent.connectorStatus === 'ONLINE' && agent.gatewayHealth !== 'UNREACHABLE'
      : agent.status === 'ACTIVE';

  const handleGeneratePlanningTasks = async () => {
    if (!planningDescription.trim()) return;
    try {
      const res = await generatePlanningTasks({
        variables: { projectDescription: planningDescription.trim(), count: 4 },
      });
      const generated = res.data?.generatePlanningTasks ?? [];
      if (generated.length === 0) {
        toast({ title: 'No tasks generated', description: 'Try a more detailed description.', status: 'warning', duration: 4000, isClosable: true });
        return;
      }
      setPlanningTasks(generated.map((t) => ({ name: t.name, task: t.task, prompt: t.prompt })));
      setExcludedTaskIdx(new Set()); // all tasks included by default
      setPlanningStep('review');
      // Best-effort: prefill the Watch name with an AI suggestion (non-blocking).
      if (!planningTitle.trim()) {
        suggestWatchTitle({ variables: { projectDescription: planningDescription.trim() } })
          .then((r) => {
            const t = r.data?.suggestWatchTitle;
            if (t) setPlanningTitle((prev) => prev.trim() || t);
          })
          .catch(() => {});
      }
    } catch {
      toast({ title: 'Could not generate tasks', description: 'Please try again.', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleCreatePlanningProbe = async () => {
    const includedTasks = planningTasks.filter((_, idx) => !excludedTaskIdx.has(idx));
    if (planningAgentIds.size === 0 || includedTasks.length === 0) return;
    try {
      const result = await createPlanningProbe({
        variables: {
          input: {
            projectDescription: planningDescription.trim(),
            title: planningTitle.trim() || undefined,
            tasks: includedTasks.map((t) => ({ name: t.name, prompt: t.prompt })),
            customAgentIds: Array.from(planningAgentIds),
            sessionId: selectedSessionId || undefined,
          },
        },
        // The Watches list is driven by the ProjectTemplates query, so refetch that
        // (awaiting it) so the new watch card appears immediately.
        refetchQueries: ['ProjectTemplates'],
        awaitRefetchQueries: true,
      });
      // Apply the chosen monitoring settings (they live on the template), same as the
      // opinion-watch flow.
      const createdTemplateId = result.data?.createPlanningProbe?.projectTemplateId;
      if (createdTemplateId) {
        updateProjectSettings({
          variables: {
            input: {
              projectTemplateId: createdTemplateId,
              updateFrequency,
              customUpdateIntervalHours: updateFrequency === UpdateFrequency.Custom ? customUpdateIntervalHours : undefined,
              autoUpdateEnabled,
              changeDetectionEnabled,
              sensitivityStdDevs,
              burnInPeriod,
              ...notificationSettingsPayload,
            },
          },
        }).catch((settingsError) => {
          console.error('Failed to apply planning-probe settings:', settingsError);
        });
      }
      toast({ title: 'Watch created', description: 'Generating the first set of plans…', status: 'success', duration: 4000, isClosable: true });
      customOnClose();
    } catch {
      toast({ title: 'Could not create watch', description: 'Please try again.', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const customOnClose = () => {
    // Reset form
    setTopic('');
    setSubtopics(['']);
    setSelectedFile(null);
    setSelectedDtType(null);
    setSelectedDemographics(new Set());
    setSelectAll(false);
    setCreateProjectLoading(false);
    setViewType('form');
    setWatchKind(null);
    setSelectedAgentId(null);
    setPersonalStep('select');
    setPlanningStep('agent');
    setWatchTitle('');
    setSelectedSessionId(null);
    setPlanningDescription('');
    setPlanningTasks([]);
    setPlanningTitle('');
    setExcludedTaskIdx(new Set());
    setPlanningAgentIds(new Set());
    setConfirmTopic('');
    setConfirmSubtopics([]);
    setProjectId(null);
    setInitialTopic('');
    setInitialSubtopics([]);
    setSearchTerm('');
    setSelectedCollectionId(null);
    setSelectedTopicIds(new Set());
    setSelectedSubtopicIds(new Set());

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={customOnClose} isCentered motionPreset="slideInBottom" scrollBehavior="inside">
      <ModalOverlay
        bg="blackAlpha.600"
        backdropFilter="blur(10px)"
      />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={{ base: 2, sm: 4 }}
        maxH={{ base: '88dvh', md: '90vh' }}
        maxW="700px"
        w={{ base: '100%', md: '700px' }}
        overflow="visible"
      >
        {/* Main modal container */}
        <Box
          bg="white"
          borderRadius={{ base: '16px', md: '20px' }}
          overflow="hidden"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          display="flex"
          flexDirection="column"
          maxH={{ base: '88dvh', md: '90vh' }}
          minH={{ base: '400px', md: '500px' }}
        >
          {/* Header */}
          <Flex
            px={6}
            py={5}
            align="center"
            justify="space-between"
            borderBottom="1px solid #f1f5f9"
          >
            <Box>
              <Text
                fontSize="18px"
                fontWeight={600}
                color="#1a1a2e"
                letterSpacing="-0.01em"
              >
                New watch
              </Text>
              <Text fontSize="13px" color="#64748b" mt={0.5}>
                {viewType === 'settings'
                  ? 'Configure monitoring and notification settings'
                  : screenFork
                    ? 'What do you want to monitor?'
                    : screenCodingAgent
                      ? 'Select the coding agent to monitor'
                      : screenCodingDescribe
                        ? 'Describe the project'
                        : screenCodingReview
                          ? 'Review the generated task set'
                          : screenPersonalSelect
                            ? 'Choose what to monitor'
                            : screenPersonaSelect
                              ? 'Choose a persona'
                              : selectedSubtopicIds.size === 0
                              ? 'Select up to 5 topics to monitor'
                              : `${selectedSubtopicIds.size} topic${selectedSubtopicIds.size > 1 ? 's' : ''} selected`}
              </Text>
            </Box>

            <ModalCloseButton
              position="relative"
              top={0}
              right={0}
              w="32px"
              h="32px"
              borderRadius="10px"
              bg="transparent"
              color="#94a3b8"
              _hover={{ bg: '#f1f5f9', color: '#64748b' }}
              transition="all 0.2s"
            />
          </Flex>

          <ModalBody flex="1" minH={0} overflowY="auto" px={6} py={5}>
          {createProjectLoading ? (
            <Flex
              width="full"
              justifyContent="center"
              alignItems="center"
              minH="300px"
            >
              <CreateChatLoading isLoading={true} isProjectCreation={true} />
            </Flex>
          ) : viewType === 'confirm' ? (
            <Flex width="full" flexDirection="column" gap={4}>
              <FormControl>
                <FormLabel>
                  <HelivanText variant="smallBody">Topic</HelivanText>
                </FormLabel>
                <Input
                  disabled={true}
                  value={confirmTopic}
                  onChange={(e) => setConfirmTopic(e.target.value)}
                  bg="white"
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <HelivanText variant="smallBody">Subtopics</HelivanText>
                </FormLabel>
                <VStack spacing={2} align="stretch">
                  {confirmSubtopics.map((subtopic, index) => (
                    <HStack key={index} spacing={2}>
                      <Input
                        value={subtopic}
                        disabled={doConfirmSubtopicsLoading}
                        onChange={(e) => {
                          const newSubtopics = [...confirmSubtopics];
                          newSubtopics[index] = e.target.value;
                          setConfirmSubtopics(newSubtopics);
                        }}
                        bg="white"
                      />
                      {confirmSubtopics.length > 3 && (
                        <IconButton
                          aria-label="Remove subtopic"
                          icon={<FaTrash />}
                          size="sm"
                          onClick={() => {
                            const newSubtopics = [...confirmSubtopics];
                            newSubtopics.splice(index, 1);
                            setConfirmSubtopics(newSubtopics);
                          }}
                          isDisabled={doConfirmSubtopicsLoading}
                        />
                      )}
                    </HStack>
                  ))}
                  {confirmSubtopics.length < 10 && (
                    <Button
                      leftIcon={<FaPlus />}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setConfirmSubtopics([...confirmSubtopics, '']);
                      }}
                      alignSelf="flex-start"
                      isDisabled={doConfirmSubtopicsLoading}
                    >
                      Add Subtopic
                    </Button>
                  )}
                </VStack>
              </FormControl>
            </Flex>
          ) : viewType === 'settings' ? (
            <VStack spacing={4} align="stretch" w="full">
              {/* Selected scope summary — tasks for coding, topics for personal */}
              <Box
                p={3}
                borderRadius="10px"
                bg="rgba(0,71,125,0.04)"
                border="1px solid rgba(0,71,125,0.1)"
              >
                <HStack flexWrap="wrap" gap={1.5}>
                  <Text fontSize="11px" fontWeight="600" color="#00477D" textTransform="uppercase" mr={1}>
                    {watchKind === 'coding' ? 'Tasks:' : 'Topics:'}
                  </Text>
                  {(watchKind === 'coding'
                    ? planningTasks.map((t) => ({ id: t.name, name: t.name }))
                    : getSelectedSubtopics()
                  ).map((st) => (
                    <Box
                      key={st.id}
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      bg="white"
                      border="1px solid #e2e8f0"
                      fontSize="12px"
                      color="#475569"
                    >
                      {st.name}
                    </Box>
                  ))}
                </HStack>
              </Box>

              {/* Estimated agent token cost (coding watches) */}
              {watchKind === 'coding' && planningTasks.length > 0 && (() => {
                const perRun = estimateRunTokens(planningTasks.map((t) => t.prompt));
                const perMonth = autoUpdateEnabled
                  ? perRun * runsPerMonth(updateFrequency, customUpdateIntervalHours)
                  : 0;
                return (
                  <Box p={3} borderRadius="10px" bg="#f8fafc" border="1px solid #e2e8f0">
                    <HStack spacing={1} mb={1}>
                      <Text fontSize="12px" fontWeight={600} color="#1a1a2e">Estimated agent token cost</Text>
                      <Tooltip label="A rough estimate of the tokens YOUR agent generates to be monitored (prompts + plans across all tasks). Excludes our backend scoring. Actuals are tracked once the watch runs." placement="top" hasArrow>
                        <span><Icon as={RiInformationLine} boxSize="12px" color="#94a3b8" cursor="help" /></span>
                      </Tooltip>
                    </HStack>
                    <Text fontSize="13px" color="#475569">
                      ~{humanizeTokens(perRun)} tokens / check-in
                      {perMonth > 0 && <> · ~{humanizeTokens(perMonth)} / month at this frequency</>}
                    </Text>
                  </Box>
                );
              })()}

              {/* Monitoring Settings - Compact */}
              <Box>
                <Text fontSize="13px" fontWeight="600" color="#1a1a2e" mb={2}>
                  Monitoring Schedule
                </Text>
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between" align="center" p={3} bg="#f8fafc" borderRadius="8px">
                    <HStack spacing={0} align="center">
                      <Text fontSize="13px" color="#475569">
                        Auto-update perspectives
                      </Text>
                      <SettingHint label="Re-run this agent on a schedule to track changes over time. We recommend daily for agents used multiple times throughout the day." />
                    </HStack>
                    <Box
                      as="button"
                      w="40px"
                      h="22px"
                      borderRadius="full"
                      bg={autoUpdateEnabled ? '#00477D' : '#e2e8f0'}
                      position="relative"
                      transition="all 0.2s"
                      onClick={() => setAutoUpdateEnabled(!autoUpdateEnabled)}
                      flexShrink={0}
                    >
                      <Box
                        position="absolute"
                        top="2px"
                        left={autoUpdateEnabled ? '20px' : '2px'}
                        w="18px"
                        h="18px"
                        borderRadius="full"
                        bg="white"
                        boxShadow="0 1px 2px rgba(0,0,0,0.2)"
                        transition="all 0.2s"
                      />
                    </Box>
                  </HStack>

                  {autoUpdateEnabled && (
                    <HStack spacing={2} flexWrap="wrap" pl={1}>
                      {[
                        { value: UpdateFrequency.ThirtyMinutes, label: '30 min' },
                        { value: UpdateFrequency.Hourly, label: 'Hourly' },
                        { value: UpdateFrequency.SixHours, label: '6 Hours' },
                        { value: UpdateFrequency.Daily, label: 'Daily' },
                        { value: UpdateFrequency.Weekly, label: 'Weekly' },
                      ].map((option) => (
                        <Box
                          key={option.value}
                          as="button"
                          px={3}
                          py={{ base: 1.5, md: 1 }}
                          borderRadius="6px"
                          fontSize={{ base: '13px', md: '12px' }}
                          fontWeight={updateFrequency === option.value ? '600' : '400'}
                          color={updateFrequency === option.value ? 'white' : '#475569'}
                          bg={updateFrequency === option.value ? '#00477D' : 'white'}
                          border="1px solid"
                          borderColor={updateFrequency === option.value ? '#00477D' : '#e2e8f0'}
                          transition="all 0.15s"
                          _hover={{ borderColor: '#00477D' }}
                          onClick={() => setUpdateFrequency(option.value)}
                        >
                          {option.label}
                        </Box>
                      ))}
                    </HStack>
                  )}
                </VStack>
              </Box>

              {/* Change Detection Settings - Compact */}
              <Box>
                <Text fontSize="13px" fontWeight="600" color="#1a1a2e" mb={2}>
                  Change Detection
                </Text>
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between" align="center" p={3} bg="#f8fafc" borderRadius="8px">
                    <HStack spacing={0} align="center">
                      <Text fontSize="13px" color="#475569">
                        Detect opinion shifts
                      </Text>
                      <SettingHint label="Flags when this agent's answers drift meaningfully from its baseline, so you're alerted to behavior changes." />
                    </HStack>
                    <Box
                      as="button"
                      w="40px"
                      h="22px"
                      borderRadius="full"
                      bg={changeDetectionEnabled ? '#00477D' : '#e2e8f0'}
                      position="relative"
                      transition="all 0.2s"
                      onClick={() => setChangeDetectionEnabled(!changeDetectionEnabled)}
                      flexShrink={0}
                    >
                      <Box
                        position="absolute"
                        top="2px"
                        left={changeDetectionEnabled ? '20px' : '2px'}
                        w="18px"
                        h="18px"
                        borderRadius="full"
                        bg="white"
                        boxShadow="0 1px 2px rgba(0,0,0,0.2)"
                        transition="all 0.2s"
                      />
                    </Box>
                  </HStack>

                  {changeDetectionEnabled && (
                    <HStack spacing={2} pl={1} align="center">
                      <Text fontSize="12px" color="#64748b" whiteSpace="nowrap">
                        Sensitivity:
                      </Text>
                      <SettingHint label="Choose high sensitivity to be alerted of small changes; low sensitivity only flags large shifts." />
                      {[
                        { value: 3.0, label: 'Low' },
                        { value: 2.0, label: 'Medium' },
                        { value: 1.0, label: 'High' },
                      ].map((option) => (
                        <Box
                          key={option.value}
                          as="button"
                          px={3}
                          py={1}
                          borderRadius="6px"
                          fontSize="12px"
                          fontWeight={sensitivityStdDevs === option.value ? '600' : '400'}
                          color={sensitivityStdDevs === option.value ? 'white' : '#475569'}
                          bg={sensitivityStdDevs === option.value ? '#00477D' : 'white'}
                          border="1px solid"
                          borderColor={sensitivityStdDevs === option.value ? '#00477D' : '#e2e8f0'}
                          transition="all 0.15s"
                          _hover={{ borderColor: '#00477D' }}
                          onClick={() => setSensitivityStdDevs(option.value)}
                        >
                          {option.label}
                        </Box>
                      ))}
                    </HStack>
                  )}
                </VStack>
              </Box>

              {/* Monitoring emails — matches the watch Settings modal */}
              <Box>
                <Text fontSize="13px" fontWeight="600" color="#1a1a2e" mb={1}>
                  Monitoring emails
                </Text>
                <Text fontSize="12px" color="#64748b" mb={2}>
                  How often do you want monitoring summaries?
                </Text>
                <VStack spacing={2} align="stretch">
                  {[
                    { value: NotificationFrequency.EveryUpdate, label: 'Every update', desc: 'Email after each completed check-in' },
                    { value: NotificationFrequency.Interval, label: 'On a schedule', desc: 'A digest summarizing recent updates' },
                    { value: NotificationFrequency.OnChange, label: 'Only on detected changes', desc: 'Email only when behavior changes are flagged' },
                    { value: NotificationFrequency.Never, label: 'Never', desc: 'No monitoring emails' },
                  ].map((opt) => {
                    const selected = notificationFrequency === opt.value;
                    return (
                      <Box
                        key={opt.value}
                        p={3}
                        borderRadius="8px"
                        border="1px solid"
                        borderColor={selected ? '#00477D' : '#e2e8f0'}
                        bg={selected ? 'rgba(0,71,125,0.06)' : 'white'}
                        cursor="pointer"
                        onClick={() => setNotificationFrequency(opt.value)}
                        _hover={{ borderColor: '#00477D' }}
                        transition="all 0.15s"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="13px" fontWeight={500} color="#1a1a2e">{opt.label}</Text>
                            <Text fontSize="11px" color="#64748b">{opt.desc}</Text>
                          </VStack>
                          {selected && <Box as="span" color="#00477D" fontSize="14px">✓</Box>}
                        </HStack>
                      </Box>
                    );
                  })}

                  {notificationFrequency === NotificationFrequency.Interval && (
                    <HStack>
                      <Text fontSize="12px" color="#64748b">Summary every</Text>
                      <Input
                        type="number"
                        min={1}
                        value={summaryIntervalHours}
                        onChange={(e) => setSummaryIntervalHours(Math.max(1, parseInt(e.target.value) || 1))}
                        size="sm"
                        width="90px"
                        bg="white"
                        borderRadius="6px"
                        borderColor="#e2e8f0"
                      />
                      <Text fontSize="12px" color="#64748b">hours</Text>
                    </HStack>
                  )}

                  {notificationFrequency !== NotificationFrequency.Never && (
                    <Input
                      placeholder="Email address (leave blank for account email)"
                      value={notificationEmail}
                      onChange={(e) => setNotificationEmail(e.target.value)}
                      size="sm"
                      borderRadius="6px"
                      borderColor="#e2e8f0"
                      fontSize="12px"
                      _hover={{ borderColor: '#cbd5e1' }}
                      _focus={{ borderColor: '#00477D', boxShadow: '0 0 0 1px #00477D' }}
                    />
                  )}
                </VStack>
              </Box>
            </VStack>
          ) : !hasTokens ? (
            <Flex
              width="full"
              flexDirection="column"
              gap={4}
              alignItems="center"
              justifyContent="center"
            >
              <HelivanText variant="largeBody" textAlign="center">
                You have no project creation tokens left. Each user gets 1
                token per week. Please reach out to info@helivan.io to inquire
                about purchasing more.
              </HelivanText>
              <HStack spacing={2}>
                <Flex
                  height="1px"
                  width="100px"
                  bg={HelivanColor.border.primary}
                />
                <HelivanText
                  variant="largeBody"
                  colorOverride={HelivanColor.border.primary}
                >
                  OR
                </HelivanText>
                <Flex
                  height="1px"
                  width="100px"
                  bg={HelivanColor.border.primary}
                />
              </HStack>
              <HelivanText variant="largeBody" textAlign="center">
                Make a request to share your watch with the AgentWatch
                community & potentially see the watch created
              </HelivanText>
            </Flex>
          ) : viewType === 'form' ? (
            <VStack spacing={6} align="stretch" w="full">
              {/* Pre-selection Header */}
              {hasPreSelection && (
                <Box p={3} bg="rgba(0,71,125,0.05)" borderRadius="8px">
                  <Text fontSize="12px" color="#64748b">Creating project for:</Text>
                  <Text fontSize="14px" fontWeight="600" color="#00477D">
                    {preSelectionName}
                  </Text>
                </Box>
              )}

              {/* SCREEN 1 — fork: choose what kind of agent to watch */}
              {screenFork && (
                <VStack spacing={3} align="stretch">
                  <Flex
                    p={4}
                    align="center"
                    justify="space-between"
                    bg="rgba(0,71,125,0.04)"
                    border="1px solid #e2e8f0"
                    borderRadius="12px"
                    cursor="pointer"
                    onClick={() => {
                      if (agentPreSelection && preSelectedCustomAgentId) {
                        setPlanningAgentIds(new Set([preSelectedCustomAgentId]));
                        setSelectedSessionId(null);
                        loadSessions({ variables: { agentId: preSelectedCustomAgentId } });
                        setPlanningStep('session'); // agent chosen → pick its session
                      } else {
                        setPlanningStep('agent');
                      }
                      setWatchKind('coding');
                    }}
                    _hover={{ borderColor: '#00477D', bg: 'rgba(0,71,125,0.07)' }}
                    transition="all 0.2s"
                  >
                    <HStack spacing={3}>
                      <Flex w="36px" h="36px" borderRadius="8px" bg="white" align="center" justify="center" border="1px solid #e2e8f0">
                        <RiTerminalBoxLine size={18} color="#00477D" />
                      </Flex>
                      <Box>
                        <Text fontSize="14px" fontWeight={600} color="#1a1a2e">Watch / monitor a coding agent</Text>
                        <Text fontSize="12px" color="#64748b">Claude Code, Codex, etc. — track its planning over time</Text>
                      </Box>
                    </HStack>
                    <Text fontSize="18px" color="#00477D">→</Text>
                  </Flex>

                  <Flex
                    p={4}
                    align="center"
                    justify="space-between"
                    bg="rgba(0,71,125,0.04)"
                    border="1px solid #e2e8f0"
                    borderRadius="12px"
                    cursor="pointer"
                    onClick={() => {
                      // An agent pre-selection means the agent is fixed → go to topics.
                      setPersonalStep(agentPreSelection ? 'topics' : 'select');
                      setWatchKind('personal');
                    }}
                    _hover={{ borderColor: '#00477D', bg: 'rgba(0,71,125,0.07)' }}
                    transition="all 0.2s"
                  >
                    <HStack spacing={3}>
                      <Flex w="36px" h="36px" borderRadius="8px" bg="white" align="center" justify="center" border="1px solid #e2e8f0">
                        <RiRobot2Line size={18} color="#00477D" />
                      </Flex>
                      <Box>
                        <Text fontSize="14px" fontWeight={600} color="#1a1a2e">Watch / monitor a personal agent</Text>
                        <Text fontSize="12px" color="#64748b">OpenClaw, Hermes, etc. — track its opinions over time</Text>
                      </Box>
                    </HStack>
                    <Text fontSize="18px" color="#00477D">→</Text>
                  </Flex>

                  <Flex
                    p={4}
                    align="center"
                    justify="space-between"
                    bg="rgba(0,71,125,0.04)"
                    border="1px solid #e2e8f0"
                    borderRadius="12px"
                    cursor="pointer"
                    onClick={() => {
                      setSelectedAgentId(null);
                      setSelectedCollectionId(null);
                      setPersonalStep('select');
                      setWatchKind('persona');
                    }}
                    _hover={{ borderColor: '#00477D', bg: 'rgba(0,71,125,0.07)' }}
                    transition="all 0.2s"
                  >
                    <HStack spacing={3}>
                      <Flex w="36px" h="36px" borderRadius="8px" bg="white" align="center" justify="center" border="1px solid #e2e8f0">
                        <RiSparkling2Line size={18} color="#00477D" />
                      </Flex>
                      <Box>
                        <Text fontSize="14px" fontWeight={600} color="#1a1a2e">Watch / monitor a persona</Text>
                        <Text fontSize="12px" color="#64748b">Curated, pre-built by the platform — track its opinions over time</Text>
                      </Box>
                    </HStack>
                    <Text fontSize="18px" color="#00477D">→</Text>
                  </Flex>
                </VStack>
              )}

              {/* SCREEN 2 (coding) — select agent(s) to monitor */}
              {screenCodingAgent && (
                <VStack spacing={4} align="stretch">
                  <Box as="button" alignSelf="flex-start" fontSize="12px" color="#64748b" onClick={() => setWatchKind(null)} _hover={{ color: '#00477D' }}>
                    ← Choose a different agent type
                  </Box>
                  <Box>
                    <Text fontSize="15px" fontWeight={600} color="#1a1a2e" mb={1}>Select the coding agent to monitor</Text>
                    <Text fontSize="13px" color="#64748b" mb={3}>Next you'll pick which of its Claude Code sessions to watch.</Text>
                    {planningAgents.length === 0 ? (
                      <Text fontSize="13px" color="#64748b">No agents found. Enroll one via the agentwatch CLI first.</Text>
                    ) : (
                      <Stack spacing={2} maxH="320px" overflowY="auto">
                        {planningAgents.map((agent) => {
                          const selected = planningAgentIds.has(agent.id);
                          const online = planningAgentOnline(agent);
                          return (
                            <Flex
                              key={agent.id}
                              p={3}
                              align="center"
                              justify="space-between"
                              bg={selected ? 'rgba(0,71,125,0.06)' : 'white'}
                              border="1px solid"
                              borderColor={selected ? '#00477D' : '#e2e8f0'}
                              borderRadius="10px"
                              cursor="pointer"
                              onClick={() => setPlanningAgentIds(new Set([agent.id]))}
                              _hover={{ borderColor: '#00477D' }}
                              transition="all 0.15s"
                            >
                              <HStack spacing={2}>
                                <RiRobot2Line size={16} color="#64748b" />
                                <Text fontSize="13px" color="#1a1a2e">{agent.name}</Text>
                                <Badge colorScheme={online ? 'green' : 'gray'} fontSize="10px">{online ? 'Online' : 'Offline'}</Badge>
                              </HStack>
                              {selected && <Text fontSize="14px" color="#00477D">✓</Text>}
                            </Flex>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </VStack>
              )}

              {/* SCREEN 2b (coding) — pick the Claude Code session to pin */}
              {screenCodingSession && (
                <VStack spacing={4} align="stretch">
                  <Box as="button" alignSelf="flex-start" fontSize="12px" color="#64748b" onClick={() => setPlanningStep('agent')} _hover={{ color: '#00477D' }}>
                    ← Back
                  </Box>
                  <Box>
                    <Text fontSize="15px" fontWeight={600} color="#1a1a2e" mb={1}>Select the session to watch</Text>
                    <Text fontSize="13px" color="#64748b" mb={3}>
                      This watch is pinned to one Claude Code session. Each check-in resumes it (read-only)
                      to track how its plan evolves. A new session means a new watch.
                    </Text>
                    {(() => {
                      const agentId = Array.from(planningAgentIds)[0];
                      const agent = planningAgents.find((a) => a.id === agentId);
                      const online = agent ? planningAgentOnline(agent) : false;
                      const sessions = sessionsData?.listConnectorSessions ?? [];
                      if (!online) {
                        return <Text fontSize="13px" color="#64748b">"{agent?.name}" must be online to list its sessions. Start its connector and go back.</Text>;
                      }
                      if (sessionsLoading) {
                        return <HStack spacing={2} py={4}><Spinner size="sm" color="#00477D" /><Text fontSize="13px" color="#64748b">Loading sessions…</Text></HStack>;
                      }
                      if (sessionsError) {
                        return <Text fontSize="13px" color="#ef4444">Couldn't list sessions: {sessionsError.message}</Text>;
                      }
                      if (sessions.length === 0) {
                        return <Text fontSize="13px" color="#64748b">No Claude Code sessions found on that machine.</Text>;
                      }
                      const allDates = sessions.map((s) => new Date(s.lastModified).toISOString());
                      return (
                        <Stack spacing={2} maxH="320px" overflowY="auto">
                          {sessions.map((s) => {
                            const sel = selectedSessionId === s.sessionId;
                            return (
                              <Flex
                                key={s.sessionId}
                                p={3}
                                align="center"
                                justify="space-between"
                                bg={sel ? 'rgba(0,71,125,0.06)' : 'white'}
                                border="1px solid"
                                borderColor={sel ? '#00477D' : '#e2e8f0'}
                                borderRadius="10px"
                                cursor="pointer"
                                onClick={() => setSelectedSessionId(s.sessionId)}
                                _hover={{ borderColor: '#00477D' }}
                                transition="all 0.15s"
                              >
                                <HStack spacing={2} minW={0}>
                                  <RiTerminalBoxLine size={16} color="#64748b" />
                                  <VStack align="start" spacing={0} minW={0}>
                                    <Text fontSize="13px" color="#1a1a2e" noOfLines={1}>{s.title}</Text>
                                    <Text fontSize="11px" color="#94a3b8">
                                      {formatVersionLabel(allDates, new Date(s.lastModified).toISOString())} · {(s.sizeBytes / 1024).toFixed(0)} KB
                                    </Text>
                                  </VStack>
                                </HStack>
                                {sel && <Text fontSize="14px" color="#00477D">✓</Text>}
                              </Flex>
                            );
                          })}
                        </Stack>
                      );
                    })()}
                  </Box>
                </VStack>
              )}

              {/* SCREEN 3 (coding) — describe the project/task */}
              {screenCodingDescribe && (
                <VStack spacing={4} align="stretch">
                  <Box
                    as="button"
                    alignSelf="flex-start"
                    fontSize="12px"
                    color="#64748b"
                    onClick={() => (agentPreSelection ? setWatchKind(null) : setPlanningStep('agent'))}
                    _hover={{ color: '#00477D' }}
                  >
                    ← Back
                  </Box>
                  <Box>
                    <Text fontSize="15px" fontWeight={600} color="#1a1a2e" mb={1}>Describe the project</Text>
                    <Text fontSize="13px" color="#64748b" mb={3}>
                      We'll generate a fixed set of tasks. Each check-in, your agent writes a plan for each
                      task and we track how its planning changes over time.
                    </Text>
                    <Textarea
                      value={planningDescription}
                      onChange={(e) => setPlanningDescription(e.target.value)}
                      placeholder="e.g. AgentWatch — a platform for monitoring AI agents over time: a NestJS + Prisma + GraphQL backend, a React (Vite + Chakra) frontend, and a Python worker that surveys agents and tracks how their plans and behavior drift…"
                      minH="180px"
                      fontSize="14px"
                      bg="white"
                      borderColor="#e2e8f0"
                      _focus={{ borderColor: '#00477D', boxShadow: 'none' }}
                    />
                  </Box>
                </VStack>
              )}

              {/* SCREEN 4 (coding) — review generated tasks */}
              {screenCodingReview && (
                <VStack spacing={4} align="stretch">
                  <Box as="button" alignSelf="flex-start" fontSize="12px" color="#64748b" onClick={() => setPlanningStep('describe')} _hover={{ color: '#00477D' }}>
                    ← Edit description
                  </Box>
                  <Box>
                    <Text fontSize="13px" fontWeight={600} color="#1a1a2e" mb={1}>Watch name</Text>
                    <Input
                      value={planningTitle}
                      onChange={(e) => setPlanningTitle(e.target.value)}
                      placeholder="Name this watch"
                      size="sm"
                      bg="white"
                      borderRadius="8px"
                      borderColor="#e2e8f0"
                      _focus={{ borderColor: '#00477D', boxShadow: 'none' }}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="15px" fontWeight={600} color="#1a1a2e" mb={1}>
                      Review the task set ({planningTasks.length - excludedTaskIdx.size} of {planningTasks.length})
                    </Text>
                    <Text fontSize="13px" color="#64748b" mb={3}>Uncheck any task you don't want to track. The rest are frozen for the watch — your agent re-plans them every check-in.</Text>
                    <Stack spacing={2}>
                      {planningTasks.map((t, idx) => {
                        const included = !excludedTaskIdx.has(idx);
                        return (
                          <Flex
                            key={idx}
                            p={3}
                            gap={3}
                            align="flex-start"
                            bg={included ? '#f8fafc' : 'white'}
                            border="1px solid"
                            borderColor={included ? '#e2e8f0' : '#eef0f3'}
                            borderRadius="10px"
                            cursor="pointer"
                            opacity={included ? 1 : 0.55}
                            onClick={() =>
                              setExcludedTaskIdx((prev) => {
                                const next = new Set(prev);
                                if (next.has(idx)) next.delete(idx);
                                else next.add(idx);
                                return next;
                              })
                            }
                            _hover={{ borderColor: '#00477D' }}
                            transition="all 0.15s"
                          >
                            <Checkbox
                              isChecked={included}
                              colorScheme="blue"
                              pointerEvents="none"
                              mt="2px"
                            />
                            <Box flex={1} minWidth={0}>
                              <Text fontSize="13px" fontWeight={600} color="#1a1a2e">{t.name}</Text>
                              <Text fontSize="12px" color="#64748b" noOfLines={3}>{t.task}</Text>
                            </Box>
                          </Flex>
                        );
                      })}
                    </Stack>
                  </Box>
                </VStack>
              )}

              {/* SCREEN 2 (personal) — back to the fork */}
              {screenPersonalSelect && (
                <Box
                  as="button"
                  alignSelf="flex-start"
                  fontSize="12px"
                  color="#64748b"
                  onClick={() => setWatchKind(null)}
                  _hover={{ color: '#00477D' }}
                >
                  ← Choose a different agent type
                </Box>
              )}
              {/* SCREEN 2 (personal) — pick your agent directly (no collection needed) */}
              {screenPersonalSelect && (
                <Box>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e" mb={3}>Choose your agent</Text>
                  {(myAgentsData?.getMyCustomAgents ?? []).length === 0 ? (
                    <Text fontSize="13px" color="#64748b" mb={2}>
                      No agents found. Enroll one via the agentwatch CLI, or monitor a collection below.
                    </Text>
                  ) : (
                    <Stack spacing={2}>
                      {(myAgentsData?.getMyCustomAgents ?? []).map((agent) => {
                        const selected = selectedAgentId === agent.id;
                        const online = planningAgentOnline(agent);
                        return (
                          <Flex
                            key={agent.id}
                            p={3}
                            align="center"
                            justify="space-between"
                            bg={selected ? 'rgba(0,71,125,0.06)' : 'white'}
                            border="1px solid"
                            borderColor={selected ? '#00477D' : '#e2e8f0'}
                            borderRadius="10px"
                            cursor="pointer"
                            onClick={() => {
                              setSelectedAgentId(selected ? null : agent.id);
                              setSelectedDtType(null);
                              setSelectedCollectionId(null);
                            }}
                            _hover={{ borderColor: '#00477D' }}
                            transition="all 0.15s"
                          >
                            <HStack spacing={2}>
                              <RiRobot2Line size={16} color="#64748b" />
                              <Text fontSize="13px" color="#1a1a2e">{agent.name}</Text>
                              <Badge colorScheme={online ? 'green' : 'gray'} fontSize="10px">{online ? 'Online' : 'Offline'}</Badge>
                            </HStack>
                            {selected && <Text fontSize="14px" color="#00477D">✓</Text>}
                          </Flex>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              )}

              {screenPersonalSelect && (
                <Box>
                  <Text fontSize="13px" fontWeight={600} color="#64748b" mb={3}>
                    Or monitor a collection (your custom group of agents or personas)
                  </Text>

                  {collections.length > 0 && (
                    <Box>
                      <Text fontSize="12px" color="#64748b" mb={2}>
                        Select a custom collection
                      </Text>
                      <StyledSelect
                        label=""
                        options={collectionOptions}
                        value={selectedCollectionId ?? ''}
                        onChange={(value) => {
                          setSelectedCollectionId(value || null);
                          if (value) {
                            setSelectedDtType(null);
                            setSelectedAgentId(null);
                          }
                        }}
                        placeholder="Custom collections..."
                      />
                    </Box>
                  )}

                  <Button
                    mt={3}
                    size="sm"
                    variant="outline"
                    leftIcon={<FaPlus size={11} />}
                    onClick={() => setCreateCollectionOpen(true)}
                  >
                    Create new collection
                  </Button>

                  <Box
                    mt={3}
                    p={3}
                    borderRadius="8px"
                    bg="#f8fafc"
                    border="1px solid #e2e8f0"
                  >
                    <Text fontSize="12px" color="#64748b" lineHeight="1.5">
                      Group your own agents (or a custom mix of personas) into a
                      collection to run a watch across them. Create one right here,
                      or from the{' '}
                      <Text as="span" fontWeight="600" color="#00477D">
                        Agents
                      </Text>{' '}
                      tab.
                    </Text>
                  </Box>

                  <CreateCollectionModal
                    isOpen={createCollectionOpen}
                    onClose={() => setCreateCollectionOpen(false)}
                    onSuccess={async (created) => {
                      await refetchCollections();
                      if (created) {
                        setSelectedCollectionId(created.id);
                        setSelectedDtType(null);
                      }
                    }}
                  />
                </Box>
              )}

              {/* SCREEN 2 (persona) — pick a curated, platform-built persona */}
              {screenPersonaSelect && (
                <Box>
                  <Box
                    as="button"
                    alignSelf="flex-start"
                    fontSize="12px"
                    color="#64748b"
                    mb={3}
                    onClick={() => setWatchKind(null)}
                    _hover={{ color: '#00477D' }}
                  >
                    ← Choose a different agent type
                  </Box>
                  <Text fontSize="15px" fontWeight={600} color="#1a1a2e" mb={1}>
                    Choose a persona
                  </Text>
                  <Text fontSize="13px" color="#64748b" mb={3}>
                    Curated, pre-built personas maintained by the platform. Next you'll pick topics to monitor.
                  </Text>
                  {dtTypeOptions.length === 0 ? (
                    <Box p={3} borderRadius="8px" bg="#f8fafc" border="1px solid #e2e8f0">
                      <Text fontSize="13px" color="#64748b">
                        No personas are available to your organization yet.
                      </Text>
                    </Box>
                  ) : (
                    <HelivanSelector
                      question=""
                      options={dtTypeOptions}
                      selectedOption={selectedDtType}
                      onSelect={handleDtTypeSelect}
                      size="lg"
                    />
                  )}
                </Box>
              )}

              {/* Offline Agent Warning (only on the topics screen) */}
              {hasOfflineAgents && screenPersonalTopics && (
                <Flex
                  bg="rgba(239,68,68,0.06)"
                  borderRadius="12px"
                  p={4}
                  gap={3}
                  align="center"
                  border="1px solid"
                  borderColor="rgba(239,68,68,0.2)"
                >
                  <RiWifiOffLine size={20} color="#ef4444" />
                  <Box flex={1}>
                    <Text fontSize="13px" fontWeight={600} color="#ef4444">
                      {offlineAgents.length === 1
                        ? `${offlineAgents[0].name} is unavailable`
                        : `${offlineAgents.length} agents are unavailable`}
                    </Text>
                    <Text fontSize="12px" color="#64748b">
                      All agents must be online with a reachable gateway to create a watch
                    </Text>
                  </Box>
                </Flex>
              )}

              {/* SCREEN 3 (personal) — select topics to monitor */}
              {screenPersonalTopics && !dtCollectionPreselect && (
                <Box
                  as="button"
                  alignSelf="flex-start"
                  fontSize="12px"
                  color="#64748b"
                  onClick={() => (agentPreSelection ? setWatchKind(null) : setPersonalStep('select'))}
                  _hover={{ color: '#00477D' }}
                >
                  ← Back
                </Box>
              )}
              {screenPersonalTopics && (
                <Box>
                  <Box mb={4}>
                    <Text fontSize="13px" fontWeight={600} color="#1a1a2e" mb={1}>Watch name</Text>
                    <Input
                      value={watchTitle}
                      onChange={(e) => setWatchTitle(e.target.value)}
                      placeholder="Name this watch (optional — defaults to the topics)"
                      size="sm"
                      bg="white"
                      borderColor="#e2e8f0"
                      _focus={{ borderColor: '#00477D', boxShadow: 'none' }}
                    />
                  </Box>
                  <HStack mb={3} spacing={2} justify="space-between">
                    <Text fontSize="15px" fontWeight="600" color="#1a1a2e">
                      Select topics to monitor
                    </Text>
                    <Text fontSize="12px" color="#64748b">
                      {selectedSubtopicIds.size}/5 selected
                    </Text>
                  </HStack>

                  <Box
                    borderRadius="16px"
                    border="1px solid #e2e8f0"
                    overflow="hidden"
                    bg="#fafafa"
                  >
                    {topicsLoading ? (
                      <Flex p={6} justify="center" align="center">
                        <Text color="#64748b" fontSize="14px">Loading topics...</Text>
                      </Flex>
                    ) : monitoringTopics.length === 0 ? (
                      <Flex p={6} justify="center" align="center">
                        <Text color="#64748b" fontSize="14px">No topics available</Text>
                      </Flex>
                    ) : (
                      <Box maxH="280px" overflowY="auto">
                        {monitoringTopics.map((topic, idx) => (
                          <Box key={topic.id}>
                            {/* Topic Header */}
                            <Flex
                              px={4}
                              py={3}
                              bg={selectedTopicIds.has(topic.id) ? 'white' : 'transparent'}
                              borderBottom={idx < monitoringTopics.length - 1 || selectedTopicIds.has(topic.id) ? '1px solid #e2e8f0' : 'none'}
                              cursor="pointer"
                              onClick={() => toggleTopic(topic.id)}
                              _hover={{ bg: 'white' }}
                              transition="all 0.15s"
                              align="center"
                              justify="space-between"
                            >
                              <HStack spacing={3}>
                                <Box
                                  w="8px"
                                  h="8px"
                                  borderRadius="full"
                                  bg={selectedTopicIds.has(topic.id) ? '#00477D' : '#cbd5e1'}
                                  transition="all 0.2s"
                                />
                                <Box>
                                  <Text fontSize="14px" fontWeight="500" color="#1a1a2e">
                                    {topic.name}
                                  </Text>
                                  {topic.description && (
                                    <Text fontSize="12px" color="#64748b" mt={0.5}>
                                      {topic.description}
                                    </Text>
                                  )}
                                </Box>
                              </HStack>
                              <Text fontSize="12px" color="#94a3b8">
                                {topic.subtopics.length}
                              </Text>
                            </Flex>

                            {/* Subtopics */}
                            {selectedTopicIds.has(topic.id) && (
                              <Box bg="white" borderBottom="1px solid #e2e8f0">
                                {topic.subtopics.map((st) => (
                                  <Flex
                                    key={st.id}
                                    px={4}
                                    py={2.5}
                                    pl={8}
                                    cursor={selectedSubtopicIds.size >= 5 && !selectedSubtopicIds.has(st.id) ? 'not-allowed' : 'pointer'}
                                    onClick={() => toggleSubtopic(st.id, topic.id)}
                                    bg={selectedSubtopicIds.has(st.id) ? 'rgba(0,71,125,0.06)' : 'transparent'}
                                    _hover={{ bg: selectedSubtopicIds.has(st.id) ? 'rgba(0,71,125,0.08)' : selectedSubtopicIds.size >= 5 ? 'transparent' : '#f8fafc' }}
                                    transition="all 0.15s"
                                    align="center"
                                    justify="space-between"
                                    opacity={selectedSubtopicIds.size >= 5 && !selectedSubtopicIds.has(st.id) ? 0.5 : 1}
                                    borderLeft={selectedSubtopicIds.has(st.id) ? '3px solid #00477D' : '3px solid transparent'}
                                  >
                                    <Text
                                      fontSize="13px"
                                      color={selectedSubtopicIds.has(st.id) ? '#00477D' : '#475569'}
                                      fontWeight={selectedSubtopicIds.has(st.id) ? '600' : '400'}
                                    >
                                      {st.name}
                                    </Text>
                                    <Box
                                      w="18px"
                                      h="18px"
                                      borderRadius="4px"
                                      border="2px solid"
                                      borderColor={selectedSubtopicIds.has(st.id) ? '#00477D' : '#cbd5e1'}
                                      bg={selectedSubtopicIds.has(st.id) ? '#00477D' : 'transparent'}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      transition="all 0.15s"
                                    >
                                      {selectedSubtopicIds.has(st.id) && (
                                        <Box as="span" color="white" fontSize="10px" fontWeight="bold">✓</Box>
                                      )}
                                    </Box>
                                  </Flex>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Selected Topics Preview */}
                  {selectedSubtopicIds.size > 0 && (() => {
                    const subtopics = getSelectedSubtopics();
                    const totalQuestions = subtopics.reduce((acc, st) => acc + st.questions.length, 0);
                    return (
                      <Box
                        mt={4}
                        p={4}
                        borderRadius="16px"
                        bg="linear-gradient(135deg, rgba(0,71,125,0.03) 0%, rgba(0,71,125,0.08) 100%)"
                        border="1px solid rgba(0,71,125,0.12)"
                      >
                        <HStack justify="space-between" align="flex-start" mb={3}>
                          <Box>
                            <Text fontSize="11px" fontWeight="600" color="#00477D" textTransform="uppercase" letterSpacing="0.05em">
                              Selected Topics
                            </Text>
                          </Box>
                          <Flex
                            px={2}
                            py={1}
                            borderRadius="full"
                            bg="rgba(0,71,125,0.1)"
                          >
                            <Text fontSize="11px" fontWeight="500" color="#00477D">
                              {totalQuestions} questions total
                            </Text>
                          </Flex>
                        </HStack>

                        {/* Selected subtopics list */}
                        <VStack spacing={2} align="stretch">
                          {subtopics.map((subtopic) => (
                            <Box
                              key={subtopic.id}
                              p={3}
                              bg="white"
                              borderRadius="10px"
                              border="1px solid #e2e8f0"
                            >
                              <HStack justify="space-between" mb={2}>
                                <Text fontSize="13px" fontWeight="600" color="#1a1a2e">
                                  {subtopic.name}
                                </Text>
                                <Text fontSize="11px" color="#64748b">
                                  {subtopic.questions.length} questions
                                </Text>
                              </HStack>
                              <Flex justify="space-between" align="center">
                                <Text fontSize="11px" color="#00477D">
                                  {subtopic.spectrumLeftTitle}
                                </Text>
                                <Box flex={1} mx={2} h="1px" bg="#e2e8f0" />
                                <Text fontSize="11px" color="#00477D">
                                  {subtopic.spectrumRightTitle}
                                </Text>
                              </Flex>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </VStack>
          ) : viewType === 'form-part2' ? (
            // Show file upload form for collection-based or non-TikToker projects
            (selectedCollectionId || selectedDtType !== DtType.Tiktoker) ? (
              <Flex width="full" flexDirection="column" gap={4}>
                <HelivanText variant="largeBody" mt={-2} textAlign="center">
                  {selectedCollectionId
                    ? 'Upload a file or add topics for your agents to discuss.'
                    : 'Upload a file for your Personas to opine on or customize your own topics below.'}
                </HelivanText>
                {/* File Upload Section */}
                <Box position="relative">
                  <Box
                    {...getRootProps()}
                    p={6}
                    border="2px dashed"
                    borderColor={
                      selectedFile
                        ? 'green.500'
                        : isDragActive
                          ? 'blue.500'
                          : 'gray.200'
                    }
                    borderRadius="md"
                    textAlign="center"
                    cursor="pointer"
                    bg={selectedFile ? 'green.50' : 'transparent'}
                    _hover={{
                      borderColor: selectedFile ? 'green.600' : 'blue.500',
                    }}
                  >
                    <input {...getInputProps()} />
                    <Icon
                      as={FaUpload}
                      w={8}
                      h={8}
                      color={selectedFile ? 'green.500' : 'gray.400'}
                      mb={2}
                    />
                    {selectedFile ? (
                      <VStack spacing={2}>
                        <Text fontWeight="bold" color="green.700">
                          File selected: {selectedFile.name}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Click to replace or drag a new file
                        </Text>
                      </VStack>
                    ) : (
                      <VStack spacing={2}>
                        <Text>
                          {isDragActive
                            ? 'Drop the file here'
                            : 'Drag and drop a file here, or click to select'}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Supported formats: PDF
                        </Text>
                      </VStack>
                    )}
                  </Box>
                  {selectedFile && (
                    <IconButton
                      aria-label="Clear file"
                      icon={<FaTrash />}
                      size="sm"
                      colorScheme="red"
                      position="absolute"
                      top={2}
                      right={2}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    />
                  )}
                </Box>

                <HStack spacing={2}>
                  <Flex
                    height="1px"
                    width="45%"
                    bg={HelivanColor.border.primary}
                  />
                  <HelivanText
                    variant="largeBody"
                    colorOverride={HelivanColor.border.primary}
                  >
                    OR
                  </HelivanText>
                  <Flex
                    height="1px"
                    width="45%"
                    bg={HelivanColor.border.primary}
                  />
                </HStack>

                {/* Monitoring Topics Selection - Single Subtopic */}
                {monitoringTopics.length > 0 && (
                  <FormControl>
                    <FormLabel>
                      <HelivanText variant="smallBody">
                        Select a Topic to Monitor
                      </HelivanText>
                    </FormLabel>
                    <Box
                      border="1px solid"
                      borderColor={HelivanColor.border.primary}
                      borderRadius="8px"
                      maxH="250px"
                      overflowY="auto"
                      bg="white"
                    >
                      {monitoringTopics.map((monitoringTopic) => (
                        <Box key={monitoringTopic.id}>
                          {/* Topic header - click to expand/collapse */}
                          <Flex
                            px={3}
                            py={2}
                            bg={selectedTopicIds.has(monitoringTopic.id) ? '#DAEFFF' : HelivanColor.background.secondary}
                            borderBottom="1px solid"
                            borderColor={HelivanColor.border.primary}
                            _hover={{ bg: '#EBF6FF', cursor: 'pointer' }}
                            onClick={() => toggleTopic(monitoringTopic.id)}
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <HelivanText variant="smallBody+" fontWeight="600">
                                {monitoringTopic.name}
                              </HelivanText>
                              {monitoringTopic.description && (
                                <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                                  {monitoringTopic.description}
                                </HelivanText>
                              )}
                            </Box>
                            <Text fontSize="sm" color={HelivanColor.text.blueMid}>
                              {monitoringTopic.subtopics.length} subtopics
                            </Text>
                          </Flex>
                          {/* Subtopics - only show when topic is selected */}
                          {selectedTopicIds.has(monitoringTopic.id) && monitoringTopic.subtopics.map((st) => (
                            <Flex
                              key={st.id}
                              px={3}
                              py={2}
                              pl={6}
                              borderBottom="1px solid"
                              borderColor={HelivanColor.border.primary}
                              _hover={{ bg: '#EBF6FF', cursor: 'pointer' }}
                              onClick={() => toggleSubtopic(st.id, monitoringTopic.id)}
                              justifyContent="space-between"
                              alignItems="center"
                              bg={selectedSubtopicIds.has(st.id) ? '#DAEFFF' : 'white'}
                            >
                              <Box flex={1}>
                                <HelivanText variant="smallBody" fontWeight={selectedSubtopicIds.has(st.id) ? '600' : '400'}>
                                  {st.name}
                                </HelivanText>
                                {st.description && (
                                  <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid} fontSize="xs">
                                    {st.description}
                                  </HelivanText>
                                )}
                              </Box>
                              <Checkbox
                                isChecked={selectedSubtopicIds.has(st.id)}
                                onChange={() => toggleSubtopic(st.id, monitoringTopic.id)}
                              />
                            </Flex>
                          ))}
                        </Box>
                      ))}
                    </Box>

                    {/* Show selected subtopics details */}
                    {selectedSubtopicIds.size > 0 && (
                      <Box mt={4} p={4} bg="#F8FAFC" borderRadius="8px" border="1px solid" borderColor={HelivanColor.border.primary}>
                        <HelivanText variant="smallBody+" fontWeight="600" mb={2}>
                          {selectedSubtopicIds.size} topic{selectedSubtopicIds.size > 1 ? 's' : ''} selected
                        </HelivanText>
                        <VStack spacing={2} align="stretch">
                          {getSelectedSubtopics().map((subtopic) => (
                            <Box key={subtopic.id} p={2} bg="white" borderRadius="6px">
                              <Text fontSize="sm" fontWeight="500" color="#1a1a2e">{subtopic.name}</Text>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </FormControl>
                )}

                {/* Custom topic input - shown if no predefined topics or as fallback */}
                {monitoringTopics.length === 0 && (
                  <>
                    <FormControl isRequired={!selectedFile && selectedSubtopicIds.size === 0}>
                      <FormLabel>
                        <HelivanText variant="smallBody">Topic {(selectedFile || selectedSubtopicIds.size > 0) && '(optional)'}</HelivanText>
                      </FormLabel>
                      <Input
                        placeholder="eg: Digital Twins"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        bg="white"
                      />
                    </FormControl>

                    {/* Subtopics (optional) - up to 5 */}
                    {(!selectedFile || topic) && (
                      <FormControl>
                        <FormLabel>
                          <HelivanText variant="smallBody">Subtopics (optional)</HelivanText>
                        </FormLabel>
                        <VStack spacing={2} align="stretch">
                          {subtopics.map((subtopic, index) => (
                            <HStack key={index}>
                              <Input
                                placeholder={`eg: Applications to social systems`}
                                value={subtopic}
                                onChange={(e) =>
                                  updateSubtopic(index, e.target.value)
                                }
                                bg="white"
                              />
                              {subtopics.length > 1 && (
                                <IconButton
                                  aria-label="Remove subtopic"
                                  icon={<FaTrash />}
                                  size="sm"
                                  onClick={() => removeSubtopic(index)}
                                />
                              )}
                            </HStack>
                          ))}
                          {subtopics.length < 5 && (
                            <Button
                              leftIcon={<FaPlus />}
                              variant="outline"
                              size="sm"
                              onClick={addSubtopic}
                              alignSelf="flex-start"
                            >
                              Add Subtopic
                            </Button>
                          )}
                        </VStack>
                      </FormControl>
                    )}
                  </>
                )}

                {/* Create Project button */}
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Flex width="full" flexDirection="column" gap={2}>
                  <HelivanText variant="smallBody" mt={-2} textAlign="center" colorOverride={HelivanColor.text.blueMid}>
                    Tell us about you/your brand.
                  </HelivanText>
                  <FormControl isRequired>
                    <FormLabel>
                      <HelivanText variant="smallBody">What is your brand name?</HelivanText>
                    </FormLabel>
                    <Input
                      placeholder="eg: Helivan"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      bg="white"
                    />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>
                      <HelivanText variant="smallBody">What is your budget? (in dollars) (optional)</HelivanText>
                    </FormLabel>
                    <Input placeholder="eg: 5000" type="number" bg="white" />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>
                      <HelivanText variant="smallBody">Enter any key words for this campaign (optional)</HelivanText>
                    </FormLabel>
                    <Textarea placeholder="eg: lifestyle, fashion, beauty, summer collection" bg="white" />
                  </FormControl>

                  <FormControl mt={4}>
                    <FormLabel>
                      <HelivanText variant="smallBody">Product(s) you are promoting (optional)</HelivanText>
                    </FormLabel>
                    <Textarea placeholder="eg: New summer clothing line, accessories" bg="white" />
                  </FormControl>
                </Flex>
              </Stack>
            )
          ) : null}
        </ModalBody>

          {/* Footer */}
          <Flex
            px={6}
            py={4}
            bg="white"
            borderTop="1px solid #f1f5f9"
            justify="flex-end"
            align="center"
          >
          {!hasTokens ? null : viewType === 'form-part2' ? (
            <>
              <HStack spacing="6px">
                <Box
                  borderRadius="8px"
                  height="12px"
                  width="48px"
                  bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                />
                <Box
                  borderRadius="8px"
                  height="12px"
                  width="20px"
                  bg="#e2e8f0"
                />
              </HStack>
              <HStack spacing={3}>
                <Box
                  as="button"
                  px={5}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="#64748b"
                  bg="white"
                  border="1px solid"
                  borderColor="#e2e8f0"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
                  _active={{ transform: 'scale(0.98)' }}
                  onClick={() => setViewType('form')}
                >
                  Back
                </Box>
                <Box
                  as="button"
                  px={6}
                  py={2.5}
                  borderRadius="12px"
                  fontSize="14px"
                  fontWeight={600}
                  color="white"
                  bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
                  cursor={
                    hasOfflineAgents ||
                    createProjectLoading ||
                    (!topic && !selectedFile && selectedSubtopicIds.size === 0) ||
                    (!selectedCollectionId && !selectedDtType) ||
                    (selectedDtType === DtType.Tiktoker && !topic)
                      ? 'not-allowed'
                      : 'pointer'
                  }
                  opacity={
                    hasOfflineAgents ||
                    createProjectLoading ||
                    (!topic && !selectedFile && selectedSubtopicIds.size === 0) ||
                    (!selectedCollectionId && !selectedDtType) ||
                    (selectedDtType === DtType.Tiktoker && !topic)
                      ? 0.6
                      : 1
                  }
                  transition="all 0.2s"
                  boxShadow="0 2px 8px rgba(0,71,125,0.3)"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
                  }}
                  _active={{ transform: 'scale(0.98)' }}
                  onClick={hasOfflineAgents ? undefined : handleCreateProject}
                >
                  {createProjectLoading ? 'Creating...' : 'Create'}
                </Box>
              </HStack>
            </>
          ) : viewType === 'confirm' ? (
            <>
              <Box />
              <Box
                as="button"
                px={6}
                py={2.5}
                borderRadius="12px"
                fontSize="14px"
                fontWeight={600}
                color="white"
                bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
                cursor={
                  doConfirmSubtopicsLoading ||
                  confirmSubtopics.filter((subtopic) => subtopic.trim() !== '').length < 3 ||
                  confirmSubtopics.some((subtopic) => subtopic.trim() === '')
                    ? 'not-allowed'
                    : 'pointer'
                }
                opacity={
                  doConfirmSubtopicsLoading ||
                  confirmSubtopics.filter((subtopic) => subtopic.trim() !== '').length < 3 ||
                  confirmSubtopics.some((subtopic) => subtopic.trim() === '')
                    ? 0.6
                    : 1
                }
                transition="all 0.2s"
                boxShadow="0 2px 8px rgba(0,71,125,0.3)"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
                }}
                _active={{ transform: 'scale(0.98)' }}
                onClick={handleConfirm}
              >
                {doConfirmSubtopicsLoading ? 'Confirming...' : 'Confirm'}
              </Box>
            </>
          ) : viewType === 'settings' ? (
            <HStack spacing={3} w="full" justify="space-between">
              <Box
                as="button"
                px={5}
                py={2.5}
                borderRadius="10px"
                fontSize="14px"
                fontWeight={600}
                color="#64748b"
                bg="white"
                border="1px solid"
                borderColor="#e2e8f0"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
                _active={{ transform: 'scale(0.98)' }}
                onClick={() => setViewType('form')}
              >
                Back
              </Box>
              <Button
                px={6}
                h="42px"
                borderRadius="10px"
                fontSize="14px"
                fontWeight={600}
                color="white"
                bg="#00477D"
                isLoading={createProjectLoading || creatingProbe}
                isDisabled={watchKind === 'coding' ? planningAgentIds.size === 0 : hasOfflineAgents}
                loadingText="Creating..."
                _hover={{
                  bg: '#003d6b',
                }}
                _active={{ transform: 'scale(0.98)' }}
                _disabled={{
                  bg: '#e2e8f0',
                  color: '#94a3b8',
                  cursor: 'not-allowed',
                  _hover: { bg: '#e2e8f0' },
                }}
                transition="all 0.15s"
                onClick={watchKind === 'coding' ? handleCreatePlanningProbe : handleCreateProject}
              >
                {watchKind === 'coding' ? 'Create watch' : 'Create Project'}
              </Button>
            </HStack>
          ) : viewType === 'form' && !screenFork ? (
            (() => {
              let label = 'Next';
              let loading = false;
              let disabled = false;
              let onClick: () => void = () => {};
              if (screenPersonalSelect) {
                disabled = !(selectedAgentId || selectedDtType || selectedCollectionId);
                onClick = () => { if (!disabled) setPersonalStep('topics'); };
              } else if (screenPersonaSelect) {
                disabled = !selectedDtType;
                onClick = () => { if (!disabled) setPersonalStep('topics'); };
              } else if (screenPersonalTopics) {
                disabled = hasOfflineAgents || !((selectedDtType || selectedCollectionId || selectedAgentId || hasPreSelection) && selectedSubtopicIds.size > 0);
                onClick = () => { if (!disabled) setViewType('settings'); };
              } else if (screenCodingAgent) {
                disabled = planningAgentIds.size === 0;
                onClick = () => {
                  if (disabled) return;
                  const agentId = Array.from(planningAgentIds)[0];
                  setSelectedSessionId(null);
                  if (agentId) loadSessions({ variables: { agentId } });
                  setPlanningStep('session');
                };
              } else if (screenCodingSession) {
                disabled = !selectedSessionId;
                onClick = () => { if (!disabled) setPlanningStep('describe'); };
              } else if (screenCodingDescribe) {
                label = generatingTasks ? 'Generating…' : 'Generate tasks';
                loading = generatingTasks;
                disabled = !planningDescription.trim() || generatingTasks;
                onClick = handleGeneratePlanningTasks;
              } else if (screenCodingReview) {
                disabled = planningTasks.length - excludedTaskIdx.size === 0;
                onClick = () => { if (!disabled) setViewType('settings'); };
              }
              return (
                <Button
                  px={6}
                  h="42px"
                  borderRadius="10px"
                  fontSize="14px"
                  fontWeight={600}
                  color="white"
                  bg="#00477D"
                  isDisabled={disabled}
                  _hover={{ bg: '#003d6b' }}
                  _active={{ transform: 'scale(0.98)' }}
                  _disabled={{
                    bg: '#e2e8f0',
                    color: '#94a3b8',
                    cursor: 'not-allowed',
                    _hover: { bg: '#e2e8f0' },
                  }}
                  transition="all 0.15s"
                  onClick={onClick}
                >
                  <HStack spacing={2}>
                    {loading && <Spinner size="xs" color="white" />}
                    <Text>{label}</Text>
                  </HStack>
                </Button>
              );
            })()
          ) : null}
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
